import type { RefObject } from "react";

import { Stack } from "@mui/material";
import { BouncingDots } from "components/BouncingDots";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { JobLogEntry } from "../lib/parseJobLogEvent";

import { useLogMarkerFilter } from "../hooks/useLogMarkerFilter";
import { useLogRoomFilter } from "../hooks/useLogRoomFilter";
import { countJobLogLevels, isLogVisibleForGranularity, type JobLogLevel } from "../model/jobLogLevels";
import { TransientJobLogContextFilter } from "./TransientJobLogContextFilter";
import { TransientJobLogDownloadButton } from "./TransientJobLogDownloadButton";
import { TransientJobLogLevelChips } from "./TransientJobLogLevelChips";
import { TransientJobLogLine } from "./TransientJobLogLine";
import { TransientJobLogRoomFilter } from "./TransientJobLogRoomFilter";

type TransientJobLogsProps = {
  bottomRef: RefObject<HTMLDivElement | null>;
  configId?: number;
  error: null | string;
  isStreaming: boolean;
  lines: JobLogEntry[];
};

export const TransientJobLogs = ({
  bottomRef,
  configId,
  error,
  isStreaming,
  lines,
}: TransientJobLogsProps) => {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<JobLogLevel>("DEBUG");
  const { hasRooms, matchesRoom, ...roomFilter } = useLogRoomFilter(lines);
  const { matches: matchesIteration, ...iterations } = useLogMarkerFilter(lines, "Iteration");
  const { matches: matchesScenario, ...scenarios } = useLogMarkerFilter(lines, "Scenario");
  const hasContext = scenarios.hasValues || iterations.hasValues;
  const contextLines = useMemo(
    () =>
      lines.filter(
        (line) => matchesRoom(line.message) && matchesScenario(line.message) && matchesIteration(line.message)
      ),
    [lines, matchesIteration, matchesRoom, matchesScenario]
  );
  const visibleLines = useMemo(
    () => contextLines.filter((line) => isLogVisibleForGranularity(line.level, granularity)),
    [contextLines, granularity]
  );
  const levelCounts = useMemo(() => countJobLogLevels(contextLines), [contextLines]);

  return (
    <div>
      <Stack className="mb-3 items-center justify-between">
        <TransientJobLogLevelChips counts={levelCounts} onSelect={setGranularity} selected={granularity} />
        <TransientJobLogDownloadButton configId={configId} />
      </Stack>
      <Stack className="mt-3 w-full items-stretch gap-3">
        {hasContext ? (
          <TransientJobLogContextFilter
            iterations={{
              isSelected: iterations.isSelected,
              onToggle: iterations.toggle,
              values: iterations.values,
            }}
            scenarios={{
              isSelected: scenarios.isSelected,
              onToggle: scenarios.toggle,
              values: scenarios.values,
            }}
          />
        ) : null}
        <div className="h-[60vh] min-w-0 flex-1 overflow-auto rounded-ds-0-5x bg-lds-foreground-strong p-6 text-lds-foreground-inverse">
          {error ? <p className="text-lds-purpose-status-negative-subtle">{error}</p> : null}
          {visibleLines.length === 0 && !isStreaming && !error && lines.length === 0 ? (
            <p className="text-lds-foreground-inverse font-ds-body-regular-md">
              {t("Execution.transient.jobDetails.logsEmpty")}
            </p>
          ) : null}
          {visibleLines.map((line, index) => (
            <TransientJobLogLine key={`${line.time ?? "line"}-${index}`} line={line} />
          ))}
          {isStreaming ? (
            <BouncingDots className="mt-4 pb-2" ref={bottomRef} />
          ) : (
            <div className="h-4" ref={bottomRef} />
          )}
        </div>
        {hasRooms ? (
          <TransientJobLogRoomFilter
            availableRooms={roomFilter.availableRooms}
            isSelected={roomFilter.isSelected}
            onSearch={roomFilter.setSearch}
            onToggle={roomFilter.toggle}
            search={roomFilter.search}
            selectedRooms={roomFilter.selectedRooms}
          />
        ) : null}
      </Stack>
    </div>
  );
};
